import { addDoc, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { PromotionalCode, promotionalCodesRef } from '@api/PromotionalCodes/PromotionalCode';
import OrderServ from '@api/Orders/OrderServ';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { describeChanges } from '@api/ActivityLogs/DescribeChanges';
import { PROMOTIONAL_CODE_FIELDS, describeDeletedPromotionalCode } from '@api/ActivityLogs/ConfigActivity';
import { buildCreationStamp } from '@api/ActivityLogs/CreationStamp';
import { getCurrentPermissionSubject } from '@utils/Permissions';
import { can, canManagePromoCode, Capability } from '@utils/Permissions/Capabilities';

export default {
    /**
     * Crea o edita un código. **Un descuento directo sobre la venta**, y hasta el
     * 2026-09-11 se podía cambiar sin rastro (M5 de la bitácora).
     *
     * ⚠️ Al CREAR sí se escribe renglón —a diferencia del catálogo, donde el
     * documento nace con su `createdAt`/`createdBy` (D9)—: aquí no hay sello de
     * creación, así que sin este renglón el código aparecería sin origen. Son unos
     * pocos códigos al mes.
     */
    async savePromotionalCode(promotionalCode: PromotionalCode): Promise<PromotionalCode> {
        promotionalCode.status = true;
        const esNuevo = !promotionalCode.id;
        // Gate (plan de Gastos F6): el admin de sucursal, solo códigos de SUS sucursales.
        const subject = getCurrentPermissionSubject();
        if (!canManagePromoCode(subject, promotionalCode)) {
            throw new Error('Solo puedes guardar códigos limitados a tus sucursales');
        }
        // La lectura previa es de la bitácora y, si no es el admin general, del gate:
        // un código de "Todas" no se vuelve suyo con solo cambiarle las sucursales.
        let anterior: PromotionalCode | undefined;
        try {
            anterior = promotionalCode.id ? await this.getPromotionalCode(promotionalCode.id) : undefined;
        } catch (error) {
            console.error('No se pudo leer el código anterior para la bitácora', error);
        }
        if (!esNuevo && !can(subject, Capability.PromoCodesManageGlobal) && (!anterior || !canManagePromoCode(subject, anterior))) {
            throw new Error('Este código no es de tus sucursales');
        }
        if (!promotionalCode.id) {
            Object.assign(promotionalCode, await buildCreationStamp());
            const data = await addDoc(promotionalCodesRef, promotionalCode);
            promotionalCode.id = data.id;
        }
        await updateDoc(doc(promotionalCodesRef, promotionalCode.id), { ...promotionalCode });
        try {
            const frase = esNuevo
                ? `creó el código promocional "${promotionalCode.code}"`
                : describeChanges(anterior || {}, promotionalCode, PROMOTIONAL_CODE_FIELDS);
            if (frase) {
                await ActivityLogServ.addActivityLog('promotionalCode', promotionalCode.id, frase);
            }
        } catch (error) {
            console.error('No se pudo registrar el cambio del código promocional', error);
        }
        return promotionalCode;
    },
    async getPromotionalCodes(): Promise<PromotionalCode[]> {
        const promotionalCodes: PromotionalCode[] = [];
        const queryDoc = query(promotionalCodesRef);
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return promotionalCodes;
        }
        snapshot.docs.map((doc: any) => {
            promotionalCodes.push(doc.data() as PromotionalCode);
        });
        const promise = promotionalCodes.map(async (promotionalCode) => {
            if (promotionalCode.id) {
                const ordersByPromotionalCode = await OrderServ.getOrdersByPromotionalCode(promotionalCode.id);
                const amount = ordersByPromotionalCode.reduce((total, order) => {
                    return total + order.promotion || 0;
                }, 0);
                promotionalCode.usage = ordersByPromotionalCode.length || 0;
                promotionalCode.usageAmount = amount || 0;
            }
        });
        await Promise.all(promise);
        return promotionalCodes;
    },
    async getPromotionalCode(promotionalCodeId: string): Promise<PromotionalCode> {
        const documentData = await getDoc(doc(promotionalCodesRef, promotionalCodeId));
        return documentData.data() as PromotionalCode;
    },
    async getPromotionalCodeByCode(code: string): Promise<PromotionalCode | undefined> {
        let promotionalCode = undefined;
        const queryPromotionalCode = query(promotionalCodesRef, where('code', '==', code));
        const snapshot = await getDocs(queryPromotionalCode);
        if (snapshot.empty) {
            return promotionalCode;
        }
        snapshot.forEach((doc: any) => {
            promotionalCode = doc.data() as PromotionalCode;
        });
        return promotionalCode;
    },
    async toggleStatus(promotionalCodeId: string): Promise<void> {
        const promotionalCode = await this.getPromotionalCode(promotionalCodeId);
        if (!canManagePromoCode(getCurrentPermissionSubject(), promotionalCode)) {
            throw new Error('Este código no es de tus sucursales');
        }
        const status = !promotionalCode.status;
        await updateDoc(doc(promotionalCodesRef, promotionalCodeId), { status });
        try {
            await ActivityLogServ.addActivityLog(
                'promotionalCode',
                promotionalCodeId,
                status ? 'activó el código' : 'desactivó el código'
            );
        } catch (error) {
            console.error('No se pudo registrar el cambio de estado del código', error);
        }
        return;
    },
    async verifyCode(code: string, branchOfficeId: string): Promise<PromotionalCode> {
        const promotionalCode = await this.getPromotionalCodeByCode(code);
        if (!promotionalCode) {
            throw new Error('Código promocional no existente');
        }
        if (!promotionalCode.status) {
            throw new Error('Código promocional deshabilitado');
        }
        if (promotionalCode.id) {
            const orders = await OrderServ.getOrdersByPromotionalCode(promotionalCode.id);
            if (!promotionalCode.noLimit && orders.length >= Number(promotionalCode.usageLimit)) {
                throw new Error('El código promocional excedio su limite de usos');
            }
        }
        if (promotionalCode.branchOfficesAvailable.includes('all')) {
            return promotionalCode;
        }
        if (!promotionalCode.branchOfficesAvailable.includes(branchOfficeId)) {
            throw new Error('Código promocional no disponible en tu sucursal');
        }
        return promotionalCode;
    },
    /**
     * ⚠️ **Limitación conocida, igual que en los borrados del catálogo**: el
     * renglón queda colgado del código que acaba de desaparecer, así que hoy
     * **no hay pantalla donde verlo**. Se escribe igual porque es barato y porque
     * el día que exista una vista de borrados ahí estará — pero que quede dicho:
     * no es una sección consultable (D5).
     */
    async deletePromotionalCode(promotionalCodeId: string): Promise<void> {
        let promotionalCode: PromotionalCode | undefined;
        try {
            promotionalCode = await this.getPromotionalCode(promotionalCodeId);
        } catch {
            // Se borra igual: la bitácora observa, no gobierna.
        }
        const subject = getCurrentPermissionSubject();
        if (!can(subject, Capability.PromoCodesManageGlobal) && (!promotionalCode || !canManagePromoCode(subject, promotionalCode))) {
            throw new Error('Este código no es de tus sucursales');
        }
        await deleteDoc(doc(promotionalCodesRef, promotionalCodeId));
        try {
            if (promotionalCode) {
                await ActivityLogServ.addActivityLog(
                    'promotionalCode',
                    promotionalCodeId,
                    describeDeletedPromotionalCode(promotionalCode)
                );
            }
        } catch (error) {
            console.error('No se pudo registrar el borrado del código promocional', error);
        }
        return;
    }
};
